import React, { forwardRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'
import { ButtonIconSquare } from '@/modules/base/components/buttons'
import IconPlus from '@assets/images/icons/add.svg'
import { formatDate } from '@/modules/_helpers/formatDate'
import SimpleAgGrid from '@/modules/base/components/simple-ag-grid/simple-ag-grid'
import ClientConfig from '@/modules/client/config'
import SimpleDrawer from '@/components/drawers/simple-drawer'
import TodoService from '@/modules/todo/todoService'
import TodoDetail from '@/modules/todo/components/todo-detail'
import TodoForm from '@/modules/todo/components/todo-form/todo-form'
import { todoStateMap, default as getState } from '@/modules/todo/components/_helpers/getState'
import TodoIcon from '@assets/images/icons/to-do.svg'
import lang from '@/lang'
import { Title } from '@/modules/base/components/texts'
import useWebSocketEvent, { EventTypes } from '@/modules/webSocket'
import todoTypesEnum from '@/enums/todo-types.enum'
import TodoSigningDrawer from '@/modules/todo/components/todo-signing-drawer'
import { Link } from 'react-router'
import { user } from 'ampersand-app'
import { useSelector } from 'react-redux'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { hasPendingUploads, getUploadProgress } from '@/modules/todo/_helpers/todoUploadHelpers'
import useClientSetting from '../../_helpers/useClientSetting'

const { clientDetail: { jumpMarkLinks } } = ClientConfig

const columns = [
  {
    field: 'status',
    headerName: lang.clientDetail.widgets.todoWidget.columns.status,
    suppressMovable: true,
    cellStyle: { display: 'flex', alignItems: 'center' },
    valueFormatter: ({ data }) => {
      return todoStateMap(data.type).get(getState(data))
    },
    width: 70,
  },
  {
    field: 'dueToDate',
    headerName: lang.clientDetail.widgets.todoWidget.columns.dueToDate,
    sort: 'desc',
    suppressMovable: true,
    valueFormatter: ({ value }) => {
      return formatDate(value)
    },
    cellStyle: { display: 'flex', alignItems: 'center' },
    width: 70,
  },
  {
    field: 'title',
    headerName: lang.clientDetail.widgets.todoWidget.columns.title,
    suppressMovable: true,
    cellStyle: { textOverFlow: 'ellipsis', display: 'block' },
    width: 200,
  },
].map((column) => ({ ...column, menuTabs: [] }))

const useStyles = makeStyles((theme) => ({
  wrapper: {
    backgroundColor: theme.white,
    width: '100%',
    height: 'min-content',
    border: theme.border,
    borderRadius: theme.borderRadius,
    padding: 30,
  },
  titleClass: {
    display: 'flex',
    alignItems: 'center',

    '& a': {
      marginLeft: 'auto',
      fontSize: theme.fontSizes.default,
    },
  },
  description: {
    margin: '10px 0',
  },
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    margin: '10px 0',
  },
  drawer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    flexGrow: 1,
  },
  formWrapper: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    '& form > div': {
      padding: '0 30px',
    },
  },
}))

const ClientTodo = forwardRef(({ client }, ref) => {
  const classes = useStyles()
  const [todos, setTodos] = useState([])
  const [selectedTodo, setSelectedTodo] = useState(null)
  const [todoEditMode, setTodoEditMode] = useState(false)
  const [todoCreateMode, setTodoCreateMode] = useState(false)
  const todoUploads = useSelector(state => state.document.upload.todoUploads)
  const { subscriptions: clientSubscriptions } = useClientSetting({ doFetch: true })

  const clientId = client?.id

  const areClientSubscriptionsExpiredOrCancelled = clientSubscriptions?.length > 0
    && clientSubscriptions.every(sub => sub.isExpired || sub.isCancelled)

  useWebSocketEvent(EventTypes.TODO_CRUD, async({ data }) => {
    if (data?.tenantCollaborationId === clientId) {
      await fetchTodos()
    }
  })

  const fetchTodos = async() => {
    const { data } = await TodoService.getAll({ collaborationId: clientId })
    if (data) setTodos([...data])
  }

  useEffect(() => { clientId && fetchTodos() }, [clientId])

  const onTodoClicked = async({ data }) => {
    if (hasPendingUploads(data.id, todoUploads)) {
      const progress = getUploadProgress(data.id, todoUploads)
      toast.showToast({
        type: 'info',
        message: lang.todo.uploadInProgressMessage(progress),
      })
      return
    }

    const todo = await TodoService.getById(data.id)
    setSelectedTodo(todo)
    const isResponsibleUser = todo?.users?.some(u => u.id === user.userId)
    if (isResponsibleUser && !todo?.isSeen) {
      await TodoService.markAsSeen(todo)
    }
  }

  const handleDrawerClose = () => {
    setSelectedTodo(null)
    setTodoEditMode(false)
    setTodoCreateMode(false)
    fetchTodos()
  }

  const renderDrawers = () => {
    if (selectedTodo && !todoEditMode && selectedTodo.type === todoTypesEnum.SIGNATURE) {
      return (
        <TodoSigningDrawer
          todo={selectedTodo}
          onClose={() => setSelectedTodo(null)} />
      )
    }
    if (selectedTodo && !todoEditMode) {
      return (
        <SimpleDrawer
          open
          noHeader
          showPager
          onClose={handleDrawerClose}
          className={classes.drawer}
        >
          <TodoDetail
            onClose={handleDrawerClose}
            id={selectedTodo.id}
            readOnly={areClientSubscriptionsExpiredOrCancelled}
            inactiveSubscription={areClientSubscriptionsExpiredOrCancelled}
            onEdit={() => {
              // Check if todo has pending uploads before editing
              if (hasPendingUploads(selectedTodo.id, todoUploads)) {
                const progress = getUploadProgress(selectedTodo.id, todoUploads)
                toast.showToast({
                  type: 'info',
                  message: lang.todo.uploadInProgressMessage(progress),
                })
                return
              }
              setTodoEditMode(true)
            }}
          />
        </SimpleDrawer>
      )
    } else if (selectedTodo && todoEditMode) {
      return (
        <SimpleDrawer
          open
          showPager
          onClose={handleDrawerClose}
          className={classes.drawer}
          headline={lang.todo.edit}
        >
          <div className={classes.formWrapper}>
            <TodoForm
              todo={selectedTodo}
              withTypeSelect
              withClientSelect
              callbackAfterSave={() => {
                setTodoEditMode(false)
                fetchTodos()
              }}
            />
          </div>
        </SimpleDrawer>
      )
    } else if (!selectedTodo && todoCreateMode) {
      return (
        <SimpleDrawer
          open
          showPager
          onClose={handleDrawerClose}
          className={classes.drawer}
          headline={lang.todo.create}
        >
          <div className={classes.formWrapper}>
            <TodoForm
              tenantCollaborationName={client?.companyName}
              tenantCollaborationId={client?.id}
              withTypeSelect
              withClientSelect
              withoutSignatureOption
              callbackAfterSave={() => {
                setTodoCreateMode(false)
                fetchTodos()
              }}
            />
          </div>
        </SimpleDrawer>
      )
    }
  }

  return (
    <div ref={ref} className={classes.wrapper} id={jumpMarkLinks.todo}>
      <Title icon={TodoIcon} className={classes.titleClass} size='small20'>
        {lang.clientDetail.widgets.todoWidget.headline}
        <Link to={`/todos?searchTerm=${client.companyName}`}>{lang.clientDetail.widgets.todoWidget.viewAll}</Link>
      </Title>
      <p className={classes.description}>{lang.clientDetail.widgets.todoWidget.description}</p>

      <SimpleAgGrid
        domLayout='autoHeight'
        defaultColDef= {{
          sortable: true,
        }}
        data={todos}
        columns={columns}
        sortingOrder={['asc', 'desc']}
        enableSorting={true}
        onRowClicked={onTodoClicked}
        localeText={{
          noRowsToShow: lang.clientDetail.widgets.todoWidget.noRowsToShow,
          loadingOoo: lang.clientDetail.widgets.todoWidget.loading,
        }}
        pagination={true}
        showBorder={false}
        paginationPageSize={5}
      />
      <div className={classes.buttonWrapper}>
        <ButtonIconSquare icon={IconPlus} label={lang.todo.create}
          onClick={() => setTodoCreateMode(true)} disabled={client.isAllowed === false || areClientSubscriptionsExpiredOrCancelled} />
      </div>

      {renderDrawers()}

    </div>
  )
})

ClientTodo.propTypes = {
  client: PropTypes.object,
}

ClientTodo.defaultProps = {
  client: {},
}

export default ClientTodo
